import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  TooltipContainer,
  HeaderLeft,
  IconCircle,
  HeaderTitle,
  HeaderActions,
  IconButton,
  TooltipDescription,
  TooltipMeta,
  MetaItem,
  MetaIcon,
  getSeverityColor,
  formatAnnotationType,
  formatDuration
} from './TooltipCommon';
import { ClockIcon, UserIcon } from './MetaIcons';
import {
  TaskHeader,
  ChipRow,
  Chip,
  SectionTitle,
  Divider,
  TaskDetails,
  TaskRow,
  TaskLabel,
  TaskValue,
  InfoCard,
  InfoCardTitle,
  InfoCardBody,
} from './TaskAnnotationTooltip.styled';
import { getAnnotationColor, getAnnotationIcon, formatAnnotationUserWithRole } from '../utils/annotationPresentation';

const TaskAnnotationTooltip = ({ annotation, position, onMouseEnter, onMouseLeave, onEdit }) => {
  const { t } = useTranslation();
  const typeColor = getAnnotationColor(annotation, { respectSeverity: false });
  const typeIcon = getAnnotationIcon(annotation.type);
  // eslint-disable-next-line no-unused-vars
  const severityColor = getSeverityColor(annotation.severity);

  const baseMetadata = annotation.base_metadata || {};
  const mergedDefect = annotation.mergedDefectLabel && annotation.mergedDefectLabel.base_metadata ? annotation.mergedDefectLabel.base_metadata : null;
  const mergedDefectDesc = annotation.mergedDefectLabel && annotation.mergedDefectLabel.description ? String(annotation.mergedDefectLabel.description) : '';
  const mergedDefectTitle = mergedDefectDesc ? mergedDefectDesc.split('\n')[0] : null;
  const mergedCondition = annotation.mergedMachineCondition && annotation.mergedMachineCondition.base_metadata ? annotation.mergedMachineCondition.base_metadata : null;

  // Decide which non-duplicate rows to render inside the Details card
  const hasRecommended = Boolean(baseMetadata.recommended_action);
  const hasComponentType = Boolean(baseMetadata.component_type || baseMetadata.component_subtype);

  // Compute a final display string for defect; only show when both name and type exist
  const defectDisplay = (() => {
    if (mergedDefectTitle) return mergedDefectTitle; // legacy merged label includes both
    const candidate = (mergedDefect && mergedDefect.defect) || baseMetadata.defect;
    if (candidate && typeof candidate === 'object' && candidate.name && candidate.type) {
      return `${candidate.name} (${candidate.type})`;
    }
    return null;
  })();

  const hasDefect = Boolean(defectDisplay);
  // Keep condition for chip logic above, but do not use it for Details section
  // const hasCondition = Boolean((mergedCondition && mergedCondition.condition) || baseMetadata.condition);
  // Do not count condition in Details, since it's already shown as a chip at top
  const hasDetails = hasRecommended || hasComponentType || hasDefect;

  // Avoid duplicate chips: if condition equals severity, hide condition chip
  const severityNormalized = String(annotation.severity || '').toLowerCase();
  const effectiveCondition = (mergedCondition && mergedCondition.condition) || baseMetadata.condition;
  const conditionNormalized = String(effectiveCondition || '').toLowerCase();
  const showConditionChip = Boolean(effectiveCondition) && conditionNormalized !== severityNormalized;

  const tooltip = (
    <TooltipContainer
      style={{ left: position.x, top: position.y }}
      placement={position.placement || 'top'}
      align={position.align || 'center'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <TaskHeader>
        <HeaderLeft>
          <IconCircle bg={typeColor}>
            <span aria-hidden="true">{typeIcon}</span>
          </IconCircle>
          <HeaderTitle>{formatAnnotationType(annotation.type)}</HeaderTitle>
        </HeaderLeft>
        <HeaderActions>
          {annotation && annotation.source_id ? (
            <IconButton
              onClick={() => window.open(`/tasks/${annotation.source_id}`, '_blank')}
              title={t('annotations.tooltip.viewDetails')}
            >
              ↗
            </IconButton>
          ) : (
            <IconButton onClick={() => (onEdit && onEdit(annotation))} title={t('annotations.tooltip.viewDetails')}>↗</IconButton>
          )}
        </HeaderActions>
      </TaskHeader>

      {baseMetadata.task_name && (
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'inherit' }}>
          {String(baseMetadata.task_name)}
        </div>
      )}

      {annotation.title && (
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'inherit' }}>
          {annotation.title}
        </div>
      )}

      <ChipRow>
        {annotation.severity && (
          <Chip
            bg={(() => {
              const s = String(annotation.severity || '').toLowerCase();
              if (s === 'critical') return '#f8caca';
              if (s === 'warning' || s === 'high' || s === 'poor') return '#ffecb3';
              if (s === 'low' || s === 'good') return '#d4edda';
              return '#e9ecef';
            })()}
            border="rgba(0,0,0,0.06)"
            color={severityColor}
          >
            {String(annotation.severity).toUpperCase()}
          </Chip>
        )}
        {baseMetadata.state && (
          <Chip>{String(baseMetadata.state)}</Chip>
        )}
        {baseMetadata.task_type && (
          <Chip>{String(baseMetadata.task_type)}</Chip>
        )}
        {showConditionChip && (
          <Chip
            bg={(() => {
              const s = String(effectiveCondition || '').toLowerCase();
              if (s === 'critical') return '#f8caca';
              if (s === 'warning' || s === 'high' || s === 'poor') return '#ffecb3';
              if (s === 'low' || s === 'good') return '#d4edda';
              return '#e9ecef';
            })()}
            color={getSeverityColor(String(effectiveCondition).toLowerCase())}
            border="rgba(0,0,0,0.06)"
          >
            {String(effectiveCondition)}
          </Chip>
        )}
      </ChipRow>

      {annotation.description && (
        <TooltipDescription>{annotation.description}</TooltipDescription>
      )}

      {hasDetails && (
        <>
          <SectionTitle>{t('annotations.tooltip.details')}</SectionTitle>
          <TaskDetails>
            {hasRecommended && (
              <InfoCard>
                <InfoCardTitle>{t('annotations.tooltip.recommendedAction')}</InfoCardTitle>
                <InfoCardBody>{String(baseMetadata.recommended_action)}</InfoCardBody>
              </InfoCard>
            )}
            {hasComponentType && (
              <TaskRow><TaskLabel>{t('annotations.tooltip.componentType')}</TaskLabel><TaskValue>{String(baseMetadata.component_type || baseMetadata.component_subtype)}</TaskValue></TaskRow>
            )}
            {hasDefect && (
              <TaskRow>
                <TaskLabel>{t('annotations.tooltip.defect')}</TaskLabel>
                <TaskValue>{defectDisplay}</TaskValue>
              </TaskRow>
            )}
            {/* Condition is intentionally not repeated here; it's already shown at the top as a chip */}
          </TaskDetails>
        </>
      )}

      <Divider />

      <TooltipMeta>
        <div className="meta-row">
          <MetaItem>
            <MetaIcon><ClockIcon size={14} /></MetaIcon>
            <span>{formatDuration(annotation.start_time, annotation.end_time)}</span>
          </MetaItem>
          <MetaItem>
            <MetaIcon><UserIcon size={14} /></MetaIcon>
            <span>{formatAnnotationUserWithRole(t, annotation)}</span>
          </MetaItem>
        </div>
      </TooltipMeta>

      {/* Bottom actions removed: header contains View Details */}
    </TooltipContainer>
  );

  return createPortal(tooltip, document.body);
};

TaskAnnotationTooltip.propTypes = {
  annotation: PropTypes.object.isRequired,
  position: PropTypes.object.isRequired,
  showControls: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onEdit: PropTypes.func
};

export default TaskAnnotationTooltip;
